import { createRegistry, type DescService } from "@bufbuild/protobuf";
import { Code, ConnectError, createClient as createConnectClient, type Client, type StreamRequest, type StreamResponse, type UnaryRequest, type UnaryResponse } from "@connectrpc/connect";
import { createConnectTransport } from "@connectrpc/connect-web";
import { SpanKind, SpanStatusCode, context, propagation, trace, type Exception, type Span } from "@opentelemetry/api";
import { ATTR_SERVER_ADDRESS } from "@opentelemetry/semantic-conventions";
import { ATTR_RPC_METHOD, ATTR_RPC_SERVICE } from "@opentelemetry/semantic-conventions/incubating";
import { SuiteSchema } from "#gen/bundles/v1/bundle_pb.js";
import { CodeSchema } from "#gen/codes/v3/codes_pb.js";
import { BadRequestSchema, DebugInfoSchema, HelpSchema, PreconditionFailureSchema, QuotaFailureSchema, RequestInfoSchema, ResourceInfoSchema, RetryInfoSchema } from "#gen/google/rpc/error_details_pb.js";
import {
	CustomDomainSetupStepPayloadSchema,
	CustomDomainSetupStepResultSchema,
	DisplayContentStepPayloadSchema,
	DisplayContentStepResultSchema,
	IntegrationsOptInStepPayloadSchema,
	IntegrationsOptInStepResultSchema,
	PixelSetupStepPayloadSchema,
	PixelSetupStepResultSchema,
	ProfileSetupStepPayloadSchema,
	ProfileSetupStepResultSchema,
	WorkspaceInviteStepPayloadSchema,
	WorkspaceInviteStepResultSchema,
	WorkspaceSetupStepPayloadSchema,
	WorkspaceSetupStepResultSchema,
} from "#gen/onboarding/v1/steps_pb.js";
import { ShortUrlSchema } from "#gen/shorturls/v1/shorturls_pb.js";
import { RETRY_STATUS_CODES } from "#lib/api/error.js";
import type { Tokens } from "#lib/frontegg.js";
import { Logger } from "#lib/logging.js";
import { activeContext } from "#lib/opentelemetry.js";

export const DEFAULT_PAGE_SIZE = 20;
export const DB_NAME_LIMIT = 120;

type InterceptorFn = (req: UnaryRequest | StreamRequest) => Promise<UnaryResponse | StreamResponse>;

export interface ClientOptions {
	credentials?: CredentialManager;
	includeCookies?: boolean;
	fetch?: typeof globalThis.fetch;
}

const registry = createRegistry(
	RequestInfoSchema,
	BadRequestSchema,
	ResourceInfoSchema,
	HelpSchema,
	DebugInfoSchema,
	RetryInfoSchema,
	QuotaFailureSchema,
	PreconditionFailureSchema,
	SuiteSchema,
	CodeSchema,
	ShortUrlSchema,
	DisplayContentStepPayloadSchema,
	DisplayContentStepResultSchema,
	WorkspaceSetupStepPayloadSchema,
	WorkspaceSetupStepResultSchema,
	ProfileSetupStepPayloadSchema,
	ProfileSetupStepResultSchema,
	CustomDomainSetupStepPayloadSchema,
	CustomDomainSetupStepResultSchema,
	WorkspaceInviteStepPayloadSchema,
	WorkspaceInviteStepResultSchema,
	IntegrationsOptInStepPayloadSchema,
	IntegrationsOptInStepResultSchema,
	PixelSetupStepPayloadSchema,
	PixelSetupStepResultSchema,
);

export class CredentialManager {
	private fetch: typeof globalThis.fetch;
	private _tokens: Tokens | null = null;
	private refreshPromise: Promise<void> | null = null;

	constructor(tokens: Tokens | null, fetch?: typeof globalThis.fetch) {
		this._tokens = tokens;
		this.fetch = fetch || globalThis.fetch;
	}

	get tokens(): Tokens | null {
		return this._tokens;
	}

	refreshTokens(): Promise<void> {
		if (!this.refreshPromise) {
			this.refreshPromise = this.refresh();
			this.refreshPromise
				.then(() => {
					this.refreshPromise = null;
				})
				.catch(err => Logger.error("refreshPromise failed while refreshing tokens", { err }));
		}
		return this.refreshPromise;
	}

	private async refresh(): Promise<void> {
		const res = await this.fetch("/oauth/refresh", {
			method: "POST",
			headers: {
				Accept: "application/json",
				"Content-Type": "application/json",
			},
			body: JSON.stringify(this._tokens),
		});
		if (res.status !== 200) {
			throw new Error("Failed to refresh tokens");
		}
		this._tokens = (await res.json()) as Tokens;
	}
}

export function createClient<T extends DescService>(address: string, service: T, options?: () => ClientOptions): Client<T> {
	return createConnectClient(
		service,
		createConnectTransport({
			baseUrl: address,
			jsonOptions: {
				ignoreUnknownFields: true,
				registry,
			},
			interceptors: [TraceInterceptor(), RetryInterceptor(options, 2), AuthInterceptor(options)],
			fetch: options?.().fetch ?? ((input, init) => fetch(input, { ...init, credentials: options?.().includeCookies ? "include" : "same-origin" })),
		}),
	);
}

function AuthInterceptor(options?: () => ClientOptions) {
	return (next: InterceptorFn) => async (req: UnaryRequest | StreamRequest) => {
		const clientOpts = options?.();
		if (clientOpts?.credentials?.tokens) {
			req.header.set("Authorization", `${clientOpts.credentials.tokens.token_type} ${clientOpts.credentials.tokens.id_token}`);
		}
		return await next(req);
	};
}

class HeaderSetter {
	set(carrier: Headers, key: string, value: string): void {
		carrier.set(key, value);
	}
}

function TraceInterceptor() {
	const tracer = trace.getTracer("connect-es", "0.1.0");
	return (next: InterceptorFn) => async (req: UnaryRequest | StreamRequest) => {
		return tracer.startActiveSpan(
			`${req.service.typeName}/${req.method.name}`,
			{
				kind: SpanKind.CLIENT,
				attributes: {
					[ATTR_RPC_METHOD]: req.method.name,
					[ATTR_RPC_SERVICE]: req.service.typeName,
					[ATTR_SERVER_ADDRESS]: req.url,
				},
			},
			activeContext(),
			async (span: Span) => {
				propagation.inject(context.active(), req.header, new HeaderSetter());
				try {
					return await next(req);
				} catch (e) {
					span.recordException(e as Exception);
					span.setStatus({ code: SpanStatusCode.ERROR });
					throw e;
				} finally {
					span.end();
				}
			},
		);
	};
}

function RetryInterceptor(options: (() => ClientOptions) | undefined, maxAttempts: number) {
	return (next: InterceptorFn) => async (req: UnaryRequest | StreamRequest) => {
		return await retry(next, req, options?.(), maxAttempts);
	};
}

async function retry(fn: InterceptorFn, req: UnaryRequest | StreamRequest, options: ClientOptions | undefined, maxAttempts: number) {
	async function execute(attempt: number): Promise<UnaryResponse | StreamResponse> {
		try {
			return await fn(req);
		} catch (err) {
			if (err instanceof ConnectError) {
				if (options?.credentials && attempt === 1 && err.code === Code.Unauthenticated) {
					const nextAttempt = attempt + 1;
					await options.credentials.refreshTokens();
					return execute(nextAttempt);
				} else if (RETRY_STATUS_CODES.has(err.code) && attempt <= maxAttempts) {
					const delayInMilliseconds = 2 ** attempt * 50; // 100, 200, 400...
					const nextAttempt = attempt + 1;
					return delay(() => execute(nextAttempt), delayInMilliseconds);
				} else {
					throw err;
				}
			} else {
				throw err;
			}
		}
	}
	return execute(1);
}

function delay(fn: () => Promise<UnaryResponse | StreamResponse>, ms: number): Promise<UnaryResponse | StreamResponse> {
	return new Promise(resolve => setTimeout(() => resolve(fn()), ms));
}

export function staticClientOptions(clientOptions: ClientOptions, includeCookies: boolean = false): () => ClientOptions {
	return () => ({ ...clientOptions, includeCookies });
}
